// The product's fields — pages, ISBN, cover type, dimensions, whatever its
// content type asks for (its field presets) and what is added here by hand —
// in the language the tab is switched to. One localized object
// (`Product-fields-<pid>`): a { key: label } row per field, the preset keys
// first in the preset's order, then the product's own; a key never repeats.
import React, { useMemo, useState } from "react"
import Button from "aldoo-ra/Button"
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { TextInput } from "../../../AdminShared/ui"
import { CreateSeries } from "../../../AdminShared/api"
import { HelperText } from "./shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"

const isMap = (value) => value && typeof value === "object" && !Array.isArray(value)

/** The preset labels of a content type, in order: { key: label }. */
export const presetsOf = (contentType) => (isMap(contentType?.fieldPresets) ? contentType.fieldPresets : {})

/** The rows of a field map, presets first: [{ key, value, preset }]. */
export const rowsOf = (fields, presets) => {
  const map = isMap(fields) ? fields : {}
  const rows = Object.keys(presets).map((key) => ({ key, value: map[key] ?? "", preset: true }))
  for (const key of Object.keys(map)) if (!(key in presets)) rows.push({ key, value: map[key] ?? "", preset: false })
  return rows
}

/**
 * The "series" field holds a reference — the slug of a series term of the
 * taxonomy (kind "series", what the importers file a book under) — not a
 * name typed in: the store page resolves it to the series' name and links
 * it, and "More from the Series" reads the same term. Picking one files the
 * product under it too (its categories), as the importers do — the store
 * lists a series by the products in it. A series the list has none of is
 * added from the search ("Add “…” as a new series"): the organization's own,
 * under its "Series" node (`vendor {series:{create}}`).
 */
export const SERIES_KEY = "series"

// `bound`: { key: { label, value, onChange } } — fields of the store product itself (the SKU,
// the EAN, from the identifiers extension). Their row is here, with the rest of the details,
// and nowhere else: it shows and writes the store product's value, the same in every
// language, and keeps the page's copy of it in every language in step. It is listed once it
// holds a value or is added (a field added with its key, "sku"); its × clears it.
// `onCategoryAdded(category)`: a series added here, for the dialog's lists (the catalog editor's)
export default function ProductFieldsEditor({ editor, contentType, categories = null, onCategoryAdded, disabled = false, bound = {} }) {
  const t = useProductEditorTexts()
  const { read, set, language } = editor
  const [seriesStatus, setSeriesStatus] = useState(null) // { text, error }
  const presets = presetsOf(contentType)
  const seriesOptions = useMemo(
    () => (categories || []).filter((c) => c.kind === "series").map((c) => ({ value: c._id, name: c.name })),
    [categories]
  )
  const fields = read("fields")
  // the store product fields asked for here (added with their button), listed while still empty
  const [shownBound, setShownBound] = useState([])
  const boundListed = (key) => String(bound[key]?.value ?? "").trim() !== "" || shownBound.includes(key)
  const rows = useMemo(() => {
    const list = rowsOf(fields, presets).filter((row) => !bound[row.key] || boundListed(row.key))
    // a store product field the page has no row of sits after the presets, before the product's own
    // fields: a field added by hand then lands where its draft row was (and keeps its focus)
    const own = list.findIndex((row) => !row.preset)
    const missing = Object.keys(bound).filter((key) => boundListed(key) && !list.some((row) => row.key === key)).map((key) => ({ key, value: "", preset: false }))
    list.splice(own < 0 ? list.length : own, 0, ...missing)
    return list.map((row) => (bound[row.key] ? { ...row, value: bound[row.key].value ?? "", bound: true } : row))
  }, [fields, presets, bound, shownBound])
  const changeBound = (key, value) => {
    bound[key].onChange(value)
    editor.setFieldEverywhere?.(key, value)
  }

  // the map this language keeps: every row with a key (a preset left empty
  // stays, so the store page's order holds; a custom row needs its key first)
  const write = (next) => {
    const map = {}
    for (const row of next) {
      const key = String(row.key || "").trim()
      if (!key || key in map) continue
      map[key] = row.value ?? ""
    }
    set("fields", map)
  }
  // a store product field's row is not the map's to write (setFieldEverywhere keeps it)
  const saved = (list) => list.filter((row) => !row.bound || (fields && row.key in fields)).map((row) => (row.bound ? { ...row, value: fields?.[row.key] ?? row.value } : row))
  // a row added by hand has no key yet, and the map keeps only keyed rows: it waits here
  // (shown after the others) until a key is typed, then it is written like any other
  const [drafts, setDrafts] = useState([])
  const change = (index, patch) => {
    // a key typed that is a store product field's ("sku"): the row becomes that field's
    const typed = String(patch.key ?? "").trim()
    if (bound[typed]) {
      const row = index >= rows.length ? drafts[index - rows.length] : rows[index]
      setShownBound([...shownBound.filter((key) => key !== typed), typed])
      if (String(row?.value ?? "").trim()) changeBound(typed, row.value)
      return remove(index)
    }
    if (index >= rows.length) {
      const d = index - rows.length
      const next = { ...drafts[d], ...patch }
      if (!String(next.key || "").trim()) return setDrafts(drafts.map((row, i) => (i === d ? next : row)))
      setDrafts(drafts.filter((_, i) => i !== d))
      return write(saved([...rows, next]))
    }
    write(saved(rows.map((row, i) => (i === index ? { ...row, ...patch } : row))))
  }
  const remove = (index) => {
    if (rows[index]?.bound) {
      changeBound(rows[index].key, "")
      return setShownBound(shownBound.filter((key) => key !== rows[index].key))
    }
    if (index >= rows.length) return setDrafts(drafts.filter((_, i) => i !== index - rows.length))
    write(rows.filter((_, i) => i !== index))
  }

  // the series picked, and the product filed under it in place of the one it was in
  const pickSeries = (index, slug) => {
    const previous = rows[index]?.value || ""
    change(index, { value: slug })
    if (previous === slug) return
    const current = read("category") || []
    const next = current.filter((id) => id !== previous || !seriesOptions.some((option) => option.value === previous))
    if (slug && !next.includes(slug)) next.push(slug)
    if (next.length !== current.length || next.some((id, i) => id !== current[i])) set("category", next, true)
  }
  // a series the list has none of: the organization's (theirs or everyone's with the name, else a new one)
  const orgId = String(editor.product?.publisher?._id || editor.product?.publisher || "")
  const addSeries = async (index, name) => {
    if (!orgId) return setSeriesStatus({ text: t`The series could not be added`, error: true })
    setSeriesStatus({ text: t`Adding…` })
    const res = await CreateSeries(orgId, name, "bg").catch(() => null)
    if (!res?.series) return setSeriesStatus({ text: res?.error === "name_invalid" ? t`Enter a series name with letters` : t`The series could not be added`, error: true })
    setSeriesStatus(null)
    onCategoryAdded?.(res.series)
    pickSeries(index, res.series._id)
  }
  const add = () => setDrafts([...drafts, { key: "", value: "", preset: false }])

  return (
    <div className="flex flex-col gap-2" data-testid="product-fields">
      {[...rows, ...drafts].map((row, index) => (
        // on a phone the label sits above its value, the × beside the value
        <div key={row.bound ? `bound-${row.key}` : row.preset ? `preset-${row.key}` : `own-${index}`} className="grid grid-cols-[1fr_auto] md:flex items-center gap-x-2 gap-y-1">
          {row.bound ? (
            <div className="col-span-2 md:col-span-1 md:w-[220px] shrink-0 text-sm text-admin_text truncate" title={t("%s: the same in every language", t(bound[row.key].label))}>
              {t(bound[row.key].label)}
              <span className="ml-1 text-xs text-admin_text_secondary">{t`all languages`}</span>
            </div>
          ) : row.preset ? (
            <div className="col-span-2 md:col-span-1 md:w-[220px] shrink-0 text-sm text-admin_text truncate" title={row.key}>
              {presets[row.key] ? t(presets[row.key]) : row.key}
              <span className="ml-1 text-xs text-admin_text_secondary">({row.key})</span>
            </div>
          ) : (
            <TextInput value={row.key} onChange={(v) => change(index, { key: v })} placeholder={t`key`} disabled={disabled} className="col-span-2 md:col-span-1 md:w-[220px] shrink-0 font-mono text-xs" data-testid={`field-key-${index}`} />
          )}
          {row.key === SERIES_KEY && categories ? (
            // the picker even with no series yet: one is added from its search
            <div className="flex-1 min-w-0" data-testid="field-value-series">
              <DropDownSelect
                variant="admin"
                // "none" is an entry of its own, so picking the chosen series
                // again keeps it rather than clearing it
                options={[{ value: "", name: t`— none —` }, ...seriesOptions]}
                value={seriesOptions.filter((option) => option.value === row.value)}
                onChange={(selection) => pickSeries(index, selection?.[0]?.value || "")}
                placeholder={t`Pick a series`}
                searchEnabled
                allowEmptySelection={false}
                showCheckbox={false}
                showSelectionTick
                disabled={disabled}
                onCreate={disabled ? undefined : (name) => (name.length >= 2 ? addSeries(index, name) : setSeriesStatus({ text: t`Enter a series name with letters`, error: true }))}
                createLabel={(name) => t("Add “%s” as a new series", name)}
              />
              {seriesStatus && <HelperText error={seriesStatus.error}>{seriesStatus.text}</HelperText>}
            </div>
          ) : row.bound ? (
            <TextInput value={row.value} onChange={(v) => changeBound(row.key, v)} placeholder={t(bound[row.key].label)} disabled={disabled} className="flex-1" data-testid={`field-${row.key}`} />
          ) : (
            <TextInput value={row.value} onChange={(v) => change(index, { value: v })} placeholder={presets[row.key] ? t(presets[row.key]) : t`value`} disabled={disabled} className="flex-1" data-testid={`field-value-${row.key || index}`} />
          )}
          <Button variant="admin_icon" className="text-admin_text_secondary hover:text-admin_error px-2" onClick={() => remove(index)} disabled={disabled} title={row.preset ? t`Leave this field out` : t`Remove`} aria-label="remove-field">
            <XMarkIcon className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
        <HelperText>
          {Object.keys(presets).length ? t("The details the product page lists, in %s. The labelled ones come from the content type; add any other with a key of your own.", language.toUpperCase()) : t("The details the product page lists, in %s. The ones come from the content type; add any other with a key of your own.", language.toUpperCase())}
        </HelperText>
        <Button variant="admin_secondary" className="h-[32px] text-xs px-3" onClick={add} disabled={disabled} data-testid="add-field">
          <PlusIcon className="w-4 h-4" /> {t`Add field`}
        </Button>
      </div>
    </div>
  )
}
